import styled, { css, keyframes } from 'styled-components';

type ResultsProps = {
  $loading: boolean;
};

const rotate = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

export const Page = styled.main`
  ${({ theme }) => css`
    min-height: calc(100dvh - 10.9rem);
    padding: ${theme.spacings[32]};
    border-top: 2px solid ${theme.colors.blue700};
    background: ${theme.colors.white};

    @media (max-width: 800px) {
      padding: ${theme.spacings[20]};
    }

    @media (max-width: 500px) {
      padding: ${theme.spacings[16]};
    }
  `}
`;

export const Container = styled.div`
  ${({ theme }) => css`
    width: min(180rem, 100%);
    margin: 0 auto;
    padding: ${theme.spacings[24]};
    border: 1px solid ${theme.colors.gray200};
    border-radius: ${theme.radius.md};
    background: ${theme.colors.gray100};
    box-shadow: 0 0.8rem 2.4rem rgba(36, 44, 92, 0.07);
  `}
`;

export const PageHeader = styled.header`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacings[32]};

    h1 {
      margin-top: ${theme.spacings[6]};
      color: ${theme.colors.indigo800};
      font-size: clamp(2.8rem, 3vw, 4rem);
      font-weight: ${theme.font.weights.bold};
      line-height: 1.1;
    }

    p {
      margin-top: ${theme.spacings[8]};
      color: ${theme.colors.gray600};
      font-size: ${theme.font.sizes.md};
    }
  `}
`;

export const Eyebrow = styled.span`
  ${({ theme }) => css`
    display: inline-flex;
    align-items: center;
    gap: ${theme.spacings[6]};
    color: ${theme.colors.blue600};
    font-size: ${theme.font.sizes.xs};
    font-weight: ${theme.font.weights.bold};
    letter-spacing: 0.08em;
    text-transform: uppercase;

    svg {
      font-size: ${theme.font.sizes.md};
    }
  `}
`;

export const SummarySection = styled.section`
  ${({ theme }) => css`
    padding: ${theme.spacings[12]};

    @media (max-width: 600px) {
      padding: ${theme.spacings[16]};
    }
  `}
`;

export const SectionHeader = styled.header`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacings[16]};

    h2 {
      color: ${theme.colors.indigo800};
      font-size: ${theme.font.sizes.xl};
      font-weight: ${theme.font.weights.bold};
    }

    p {
      margin-top: ${theme.spacings[4]};
      color: ${theme.colors.gray600};
      font-size: ${theme.font.sizes.xs};
    }
  `}
`;

export const GroupHeader = styled.header`
  ${({ theme }) => css`
    margin: ${theme.spacings[48]} 0 ${theme.spacings[20]};
    padding-top: ${theme.spacings[24]};
    border-top: 1px solid #dbe2ec;

    > span {
      color: ${theme.colors.blue600};
      font-size: ${theme.font.sizes.xs};
      font-weight: ${theme.font.weights.bold};
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    h2 {
      margin-top: ${theme.spacings[4]};
      color: ${theme.colors.indigo800};
      font-size: clamp(2rem, 2vw, 2.8rem);
      font-weight: ${theme.font.weights.bold};
    }

    p {
      margin-top: ${theme.spacings[6]};
      color: ${theme.colors.gray600};
      font-size: ${theme.font.sizes.sm};
    }
  `}
`;

export const NumberedSection = styled.section`
  ${({ theme }) => css`
    padding: ${theme.spacings[12]};
    border-radius: ${theme.radius.md};
    margin-top: ${theme.spacings[24]};

    @media (max-width: 600px) {
      padding: ${theme.spacings[16]};
    }
  `}
`;

export const NumberedSectionHeader = styled.header`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacings[16]};
    display: flex;
    align-items: flex-start;
    gap: ${theme.spacings[12]};

    h3 {
      color: ${theme.colors.indigo800};
      font-size: ${theme.font.sizes.lg};
      font-weight: ${theme.font.weights.bold};
      line-height: 1.3;
    }

    p {
      margin-top: ${theme.spacings[4]};
      color: ${theme.colors.gray600};
      font-size: ${theme.font.sizes.xs};
      line-height: 1.4;
    }
  `}
`;

export const SectionNumber = styled.span`
  ${({ theme }) => css`
    width: 3.2rem;
    height: 3.2rem;
    flex: 0 0 3.2rem;
    display: grid;
    place-items: center;
    border-radius: ${theme.radius.full};
    background: ${theme.colors.indigo800};
    color: ${theme.colors.white};
    font-size: ${theme.font.sizes.sm};
    font-weight: ${theme.font.weights.bold};
  `}
`;

export const ErrorBanner = styled.div`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacings[20]};
    padding: ${theme.spacings[16]};
    border: 1px solid #ffc6c6;
    border-left: 5px solid ${theme.colors.error};
    border-radius: ${theme.radius.sm};
    background: #fff4f4;
    color: #8c2424;
    font-size: ${theme.font.sizes.sm};
  `}
`;

export const LoadingPanel = styled.div`
  ${({ theme }) => css`
    min-height: 20rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${theme.spacings[12]};
    color: ${theme.colors.gray600};

    svg {
      color: ${theme.colors.blue600};
      font-size: 3.2rem;
      animation: ${rotate} 0.9s linear infinite;
    }

    strong {
      font-size: ${theme.font.sizes.sm};
      font-weight: ${theme.font.weights.medium};
    }
  `}
`;

export const Results = styled.div<ResultsProps>`
  ${({ $loading }) => css`
    opacity: ${$loading ? 0.58 : 1};
    pointer-events: ${$loading ? 'none' : 'auto'};
    transition: opacity 0.2s;
  `}
`;

export const CardsGrid = styled.div`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: ${theme.spacings[20]};

    @media (max-width: 1250px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 620px) {
      grid-template-columns: 1fr;
    }
  `}
`;

export const ChartsGrid = styled.div`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: start;
    gap: ${theme.spacings[20]};

    @media (max-width: 1050px) {
      grid-template-columns: 1fr;
    }
  `}
`;

export const FullChart = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
`;

export const LocationResults = styled.div`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: ${theme.spacings[20]};
  `}
`;

export const MixedGrid = styled.div`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: start;
    gap: ${theme.spacings[20]};

    @media (max-width: 1050px) {
      grid-template-columns: 1fr;
    }
  `}
`;

export const SideCards = styled.div`
  ${({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${theme.spacings[20]};

    @media (max-width: 620px) {
      grid-template-columns: 1fr;
    }
  `}
`;
